import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const Invisalign = () => {
  // FAQ Schema for Voice Search Optimization
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does Invisalign cost in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Every Invisalign case is unique — pricing depends on the complexity of your bite, length of treatment, and aligner count. Rather than publish prices that won't apply to your situation, Fort Worth Dental provides a personalized written quote at your FREE Invisalign consultation. Flexible CareCredit financing is available, and many dental insurance plans cover Invisalign."
        }
      },
      {
        "@type": "Question",
        "name": "How long does Invisalign treatment take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most Invisalign treatments take 12 to 18 months, though some cases can be completed in as little as 6 months. Treatment time depends on the complexity of your case. You'll wear each set of aligners for 1-2 weeks before switching to the next set."
        }
      },
      {
        "@type": "Question",
        "name": "Is Invisalign as effective as traditional braces?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, Invisalign is highly effective for treating most orthodontic issues including crowding, spacing, overbite, underbite, and crossbite. For complex cases, traditional braces may be recommended. Dr. Razo at Fort Worth Dental can determine if Invisalign is right for your specific needs."
        }
      },
      {
        "@type": "Question",
        "name": "Can adults get Invisalign?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely! Invisalign is extremely popular with adults because the clear aligners are virtually invisible. Many professionals choose Invisalign because they can straighten their teeth discreetly without metal braces affecting their appearance at work."
        }
      },
      {
        "@type": "Question",
        "name": "Does Invisalign hurt?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Invisalign is generally more comfortable than traditional braces. You may experience mild pressure or discomfort when switching to a new set of aligners, but this typically subsides within a day or two. There are no metal brackets or wires to irritate your cheeks and gums."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Invisalign Fort Worth TX | Clear Aligners | Free Consultation | Fort Worth Dental</title>
        <meta name="description" content="Invisalign clear aligners in Fort Worth TX by Dr. Razo. Straighten teeth without metal braces. FREE Invisalign consultation. Serving Fort Worth, Aledo, Benbrook. Call (817) 985-3337." />
        <meta name="keywords" content="invisalign fort worth, clear aligners fort worth tx, invisalign dentist, invisible braces, teeth straightening fort worth, invisalign cost fort worth, how much does invisalign cost, invisalign near me" />
        <link rel="canonical" href="https://fortworthdental.com/invisalign" />
        <meta property="og:url" content="https://fortworthdental.com/invisalign" />
        <meta property="og:title" content="Invisalign Fort Worth TX | Fort Worth Dental" />
        <meta property="og:description" content="Invisalign clear aligners by Dr. Razo. Straighten teeth without metal braces. FREE consultation." />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-purple-600 to-purple-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Invisalign® in Fort Worth, TX</h1>
          <p className="text-xl max-w-3xl mx-auto">Smile with Confidence - Clear Aligners for a Perfect Smile</p>
          <p className="text-sm mt-4 opacity-90">Affordable Invisalign Treatment | Clear Braces Alternative | Fort Worth & Aledo</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* YouTube Video Section */}
          <section className="mb-12">
            <div className="aspect-video w-full max-w-3xl mx-auto rounded-lg overflow-hidden shadow-lg">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/phPxGtax2LI"
                title="Invisalign - How It Works"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Do You Have Crooked Teeth but Don't Want Ugly Metal Braces?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              Invisalign® is the clear and discreet way to straighten your teeth. Invisalign® uses clear aligners in stages 
              instead of the traditional metal bracket wired system to help move your teeth into proper position. Invisalign® 
              is great for both teenagers and even adults.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              As the name Invisalign® implies, this alternative to braces is nearly invisible and also an affordable teeth 
              straightening option.
            </p>
          </section>

          {/* How does Invisalign work */}
          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/invisalign-clear-aligner.jpg" 
                  alt="Invisalign clear aligner tray - Transparent teeth straightening aligners custom-made for gradual tooth movement at Fort Worth Dental" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-2.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">How does Invisalign® work?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  After taking the necessary measurements and sending vital information to the Invisalign® lab, we'll receive a series of clear, customized aligners that have been designed to represent each stage of your smile as teeth move into their corrected positions. All you have to do is wear each aligner for the appropriate amount of time before switching to the next one in the series. You'll also attend brief check-ups here at our Fort Worth, TX office so we can determine that you're making adequate progress.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Only a few of us are born with perfect smiles. Many young people have metal braces while they're in middle school or high school allowing them to achieve an improved look by the time they're adults, but if you're an adult who missed this boat, you may feel like the chance is gone. After all, the last thing people in the professional world want is a mouthful of metal that could last for years. Thankfully, our team here at Fort Worth Dental offers another, more sophisticated option – Invisalign®. As the name implies, Invisalign® can give you the aligned smile you've been dreaming of invisibly.
                </p>
              </div>
            </div>
          </section>

          {/* Invisalign Benefits */}
          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Invisalign® offers patients countless benefits when compared to traditional braces. Their seamless appearance means that those around you won't even notice them – only the gorgeous improvements that follow! Additionally, they're removable, so you can practice regular oral hygiene and eat your favorite foods throughout treatment. Best of all, alignment is typically achieved in 8-18 months, which is a significant improvement over the multiple years that regular orthodontics often require.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Are you ready to make a change? Contact Fort Worth Dental today, if you have any further questions about Invisalign® or if you're ready to schedule a consultation so. We love giving our patients new reasons to smile! Our Ft. Worth, TX dental practice welcomes patients from throughout the DFW Metroplex.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/invisalign-new-2.jpg" 
                  alt="Invisalign results - Beautiful straight smile achieved with clear aligners showing before and after teeth straightening at Fort Worth Dental" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-2.png'}
                />
              </div>
            </div>
          </section>

          <section className="bg-teal-50 p-8 rounded-lg mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Invisalign® Compared To Metal Braces</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-2 text-xl">✓</span>
                <span><strong>Nearly Invisible:</strong> Made from clear plastic material - no metal in your mouth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-2 text-xl">✓</span>
                <span><strong>Removable:</strong> You can eat normally and perform normal oral hygiene</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-2 text-xl">✓</span>
                <span><strong>Comfortable:</strong> Smooth plastic that doesn't irritate gums, tongue, or lips</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-2 text-xl">✓</span>
                <span><strong>Simple Process:</strong> Routine scheduled orthodontics process that's easy to follow</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-12 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Invisalign®?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              Serving Fort Worth, Aledo, Benbrook, and surrounding areas with expert Invisalign treatment. 
              Our experienced team has helped hundreds of patients achieve their dream smile with clear aligners.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">FREE</div>
                <p className="font-semibold">Consultation</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Payment Plans</p>
              </div>
            </div>
          </section>

          <section className="text-center bg-gradient-to-r from-teal-600 to-teal-700 text-white p-12 rounded-lg mb-12">
            <h2 className="text-3xl font-bold mb-4">FREE Invisalign® Consultation</h2>
            <p className="text-xl mb-8">Start your journey to a beautiful, straight smile today!</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a href="tel:8179853337" className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 inline-block">
                Call (817) 985-3337
              </a>
              <Link to="/#book-appointment" className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700 inline-block">
                Book Online
              </Link>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your FREE Invisalign® Consultation
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Proudly serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Invisalign;
